<template>
  <div class="review-page">
 
    <!-- 加载遮罩 -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-inner">
        <div class="spinner"></div>
        <p>Loading design...</p>
      </div>
    </div>
 
    <!-- Toast 通知 -->
    <transition name="toast">
      <div v-if="toastMsg" :class="['toast', toastError ? 'toast-error' : 'toast-success']">
        {{ toastMsg }}
      </div>
    </transition>
 
    <div class="container">
      <div class="page-layout">
 
        <!-- 左侧：3D 只读预览 -->
        <div class="viewer-wrapper">
          <Viewer3D
            ref="viewer3d"
            :view-only="true"
            @model-loaded="onModelLoaded"
          />
        </div>
 
        <!-- 右侧面板 -->
        <div class="right-panel">
 
          <!-- 款式选择（点击切换3D预览） -->
          <div class="panel-block product-grid-block">
            <ProductGrid @select-garment="handleSelectGarment" />
          </div>
 
          <!-- 评论区板块 -->
          <div class="panel-block comment-block">
            <Comments 
              ref="comments"
              :current-garment="currentGarment"
            />
          </div>
 
          <!-- 名单管理板块 -->
          <div class="panel-block roster-block">
            <RosterManager ref="rosterManager" />
          </div>
 
        </div>
      </div>
    </div>
  </div>
</template>
 
<script>
import { useDesignStore } from '@/stores/designStore'
import { mapActions, mapState } from 'pinia'
import Viewer3D from '@/components/Viewer3D.vue'
import ProductGrid from '@/components/ProductGrid.vue'
import Comments from '@/components/Comments.vue'
import RosterManager from '@/components/RosterManager.vue'
 
export default {
  name: 'ReviewPage',
 
  components: { 
    Viewer3D, 
    ProductGrid, 
    Comments,
    RosterManager 
  },
 
  data() {
    return {
      isLoading: false,
      toastMsg: '',
      toastError: false,
      _toastTimer: null,
      _pendingDesignResult: null,
    }
  },
 
  computed: {
    ...mapState(useDesignStore, ['currentGarment'])
  },
 
  mounted() {
    this.init()
  },
 
  methods: {
    ...mapActions(useDesignStore, ['loadDesignFromData', 'switchGarment']),
 
    async init() {
      const params = new URLSearchParams(window.location.search)
      const id = params.get('id')
      
      // 如果没有 design id，直接显示默认款式
      if (!id) {
        await this.$nextTick()
        if (this.$refs.viewer3d) {
          this.$refs.viewer3d.loadGarment('Short Sleeve')
        }
        return
      }

      this.isLoading = true
      const store = useDesignStore()
      
      try {
        // 调用 store 的统一方法，加载评论数据
        await store.loadSharedDesign(id, {
          targetGarment: null,  // null 表示使用后端返回的款式
          loadComments: true,    // 这个页面需要加载评论
        })
        
        // 加载 3D 模型
        if (this.$refs.viewer3d) {
          this.$refs.viewer3d.loadGarment(store.currentGarment)
        }
        
        // 等待模型加载完成后，加载评论
        await this.$nextTick()
        if (this.$refs.comments && typeof this.$refs.comments.loadComments === 'function') {
          this.$refs.comments.loadComments()
        }
        
      } catch (e) {
        console.error('Load error:', e)
        this.showToast('Failed to load design', true)
      } finally {
        this.isLoading = false
      }
    },
 
    // 模型加载完成后，重新合成所有部位纹理（含文字/logo）
    onModelLoaded() {
      if (!this._pendingDesignResult) return
      const result = this._pendingDesignResult
      const targetGarment = result.current_garment || 'Short Sleeve'
      if (window.$viewer3D) {
        const garmentData = result.all_garments?.[targetGarment]
        if (garmentData?.layers) {
          for (const part of Object.keys(garmentData.layers)) {
            window.$viewer3D.compositePartTexture(part, null, false)
          }
        }
      }
      this._pendingDesignResult = null
    },
 
    handleSelectGarment(garmentType) {
      console.log('ReviewPage: 切换款式到', garmentType)
      // 更新 store 中的当前款式
      this.switchGarment(garmentType)
      // 通知 Viewer3D 加载新模型
      if (this.$refs.viewer3d) {
        this.$refs.viewer3d.loadGarment(garmentType)
      }
    },
 
    showToast(msg, isError = false) {
      this.toastMsg = msg
      this.toastError = isError
      if (this._toastTimer) clearTimeout(this._toastTimer)
      this._toastTimer = setTimeout(() => { this.toastMsg = '' }, 3000)
    },
  },
}
</script>
 
<style scoped>
.review-page {
  font-family: 'Inter', sans-serif;
  background: #ffffff;
  color: #333;
  min-height: 100vh;
}
 
/* ── 加载遮罩 ── */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}
.loading-inner {
  text-align: center;
  color: #fff;
}
.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 12px;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
 
/* ── Toast ── */
.toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #fff;
  z-index: 10001;
  pointer-events: none;
}
.toast-success { background: #10b981; }
.toast-error   { background: #ef4444; }
.toast-enter-active, .toast-leave-active { transition: opacity 0.3s, transform 0.3s; }
.toast-enter-from, .toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(10px); }
 
/* ── 布局 ── */
.container {
  width: 95%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem 0;
}
 
.page-layout {
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  align-items: flex-start;
}
 
.viewer-wrapper {
  flex: 1;
  min-width: 280px;
}
 
.right-panel {
  flex: 1;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}
 
.panel-block {
  background: #f5f5f5;
  border-radius: 10px;
  padding: 1rem;
}
 
/* 产品网格区块样式 */
.product-grid-block {
  background: #ffffff;
  border: 1px solid #e0e0e0;
}
 
/* 评论区区块样式 */
.comment-block {
  background: #eff6ff;
  border: 1px solid #dbeafe;
}
 
/* 名单管理区块样式 */
.roster-block {
  background: #f0fdf4;
  border: 1px solid #dcfce7;
}
 
/* ── 响应式 ── */
@media (max-width: 900px) {
  .page-layout {
    flex-direction: column;
  }
  .viewer-wrapper,
  .right-panel {
    width: 100%;
    min-width: 0;
  }
}
 
@media (max-width: 850px) {
  .container {
    width: 100%;
    padding: 0.5rem;
  }
  .panel-block {
    padding: 0.6rem;
  }
}
</style>